import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Briefcase, MapPin, Mail, Phone, Clock, Users, Heart, ChevronRight, Upload, X, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getVacancies, createApplication, uploadFile } from "@/lib/api";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import RecaptchaWrapper from "@/components/RecaptchaWrapper";

const HERO_IMAGE = "https://images.pexels.com/photos/7648071/pexels-photo-7648071.jpeg";

const typeLabels = {
  trainer: { label: "Trainer", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  volunteer: { label: "Vrijwilliger", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  staff: { label: "Staff", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" }
};

const typeIcons = {
  trainer: Users,
  volunteer: Heart,
  staff: Briefcase
};

const emptyApplication = {
  name: "",
  email: "",
  phone: "",
  motivation: "",
  cv_url: "",
  cv_filename: ""
};

function VacaturesPage() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applicationForm, setApplicationForm] = useState(emptyApplication);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { verifyRecaptcha } = useRecaptcha();

  useEffect(() => {
    const fetchVacancies = async () => {
      try {
        const res = await getVacancies({ active_only: true });
        setVacancies(res.data);
      } catch (error) {
        console.error("Error fetching vacancies:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVacancies();
  }, []);

  const handleViewVacancy = (vacancy) => {
    setSelectedVacancy(vacancy);
    setDetailDialogOpen(true);
  };

  const handleStartApplication = (vacancy) => {
    setSelectedVacancy(vacancy);
    setApplicationForm(emptyApplication);
    setDetailDialogOpen(false);
    setApplyDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Alleen PDF en Word documenten zijn toegestaan");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand mag maximaal 5MB zijn");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFile(file);
      setApplicationForm({
        ...applicationForm,
        cv_url: res.data.url,
        cv_filename: file.name
      });
      toast.success("CV geüpload");
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Kon CV niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCV = () => {
    setApplicationForm({
      ...applicationForm,
      cv_url: "",
      cv_filename: ""
    });
  };

  const handleSubmitApplication = async () => {
    if (!applicationForm.name || !applicationForm.email || !applicationForm.motivation) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    setSubmitting(true);
    try {
      // Verify reCAPTCHA first
      const recaptchaToken = await verifyRecaptcha("job_application");
      
      await createApplication({
        vacancy_id: selectedVacancy.id,
        vacancy_title: selectedVacancy.title,
        name: applicationForm.name,
        email: applicationForm.email,
        phone: applicationForm.phone || null,
        motivation: applicationForm.motivation,
        cv_url: applicationForm.cv_url || null,
        recaptcha_token: recaptchaToken
      });
      toast.success("Sollicitatie verstuurd! We nemen zo snel mogelijk contact met je op.");
      setApplyDialogOpen(false);
      setApplicationForm(emptyApplication);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Kon sollicitatie niet versturen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="vacatures-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">VACATURES</h1>
            <p className="text-[hsl(48,96%,53%)] font-medium mt-2">Word deel van ons team</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <Card className={`mb-12 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
          <CardContent className="py-8">
            <p className={`text-lg leading-relaxed text-center max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Futsal Gent is altijd op zoek naar gepassioneerde mensen om ons team te versterken. 
              Of je nu ervaring hebt als trainer, graag wilt helpen als vrijwilliger, of je 
              professionele vaardigheden wilt inzetten - er is altijd een plek voor jou!
            </p>
          </CardContent>
        </Card>

        {/* Vacancies Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className={`animate-pulse ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
                <CardContent className="pt-6">
                  <div className={`h-6 w-3/4 mb-4 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <div className={`h-4 w-full mb-2 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <div className={`h-4 w-2/3 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : vacancies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vacancies.map((vacancy) => {
              const typeInfo = typeLabels[vacancy.type] || typeLabels.volunteer;
              const TypeIcon = typeIcons[vacancy.type] || Briefcase;
              return (
                <Card 
                  key={vacancy.id} 
                  className={`group cursor-pointer transition-all hover:scale-[1.02] ${isDark ? 'bg-slate-900/50 border-white/10 hover:border-[hsl(217,91%,60%)]/50' : 'bg-white border-slate-200 shadow-md hover:shadow-lg'}`}
                  onClick={() => handleViewVacancy(vacancy)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-[hsl(217,91%,60%)]/20' : 'bg-blue-100'}`}>
                        <TypeIcon className={`h-6 w-6 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                      </div>
                      <Badge className={typeInfo.color}>
                        {typeInfo.label}
                      </Badge>
                    </div>
                    <CardTitle className={`font-['Chivo'] text-xl mt-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {vacancy.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-sm mb-4 line-clamp-3 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {vacancy.description}
                    </p>
                    <div className="space-y-2">
                      <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        <MapPin className="h-4 w-4" />
                        <span>{vacancy.location}</span>
                      </div>
                      {vacancy.deadline && (
                        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                          <Clock className="h-4 w-4" />
                          <span>Deadline: {vacancy.deadline}</span>
                        </div>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 mt-4 text-sm font-medium ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'} group-hover:gap-2 transition-all`}>
                      <span>Meer info</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className={`text-center py-16 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardContent>
              <Briefcase className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Geen openstaande vacatures
              </h3>
              <p className={`max-w-md mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Momenteel hebben we geen openstaande vacatures. Houd deze pagina in de gaten of 
                neem contact met ons op voor spontane sollicitaties.
              </p>
              <Button className="mt-6" asChild>
                <a href="mailto:info@futsalgent.be">Spontaan Solliciteren</a>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Call to Action */}
        <Card className={`mt-12 ${isDark ? 'bg-gradient-to-r from-blue-900/50 to-slate-900/50 border-white/10' : 'bg-gradient-to-r from-blue-50 to-slate-50 border-slate-200 shadow-md'}`}>
          <CardContent className="py-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className={`text-2xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Zit jouw droompositie er niet bij?
                </h3>
                <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  We staan altijd open voor enthousiaste mensen die iets kunnen bijdragen aan onze club. 
                  Stuur ons een open sollicitatie en vertel ons wat jij kunt betekenen voor Futsal Gent!
                </p>
              </div>
              <div className={`p-6 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-white'}`}>
                <h4 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Contact</h4>
                <div className="space-y-3">
                  <div className={`flex items-center gap-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <Mail className="h-5 w-5" />
                    <a href="mailto:info@futsalgent.be" className="hover:underline">info@futsalgent.be</a>
                  </div>
                  <div className={`flex items-center gap-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <Phone className="h-5 w-5" />
                    <span>+32 477 98 70 47</span>
                  </div>
                  <div className={`flex items-center gap-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <MapPin className="h-5 w-5" />
                    <span>Ter Linden 29, 9052 Gent</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vacancy Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
          {selectedVacancy && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={typeLabels[selectedVacancy.type]?.color || typeLabels.volunteer.color}>
                    {typeLabels[selectedVacancy.type]?.label || "Vrijwilliger"}
                  </Badge>
                </div>
                <DialogTitle className={`font-['Chivo'] text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {selectedVacancy.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                <div>
                  <h4 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Beschrijving</h4>
                  <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    {selectedVacancy.description}
                  </p>
                </div>
                
                {selectedVacancy.requirements && (
                  <div>
                    <h4 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Vereisten</h4>
                    <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'} whitespace-pre-line`}>
                      {selectedVacancy.requirements}
                    </p>
                  </div>
                )}
                
                <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                  <h4 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Contact & Locatie</h4>
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <MapPin className="h-4 w-4" />
                      <span>{selectedVacancy.location}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <Mail className="h-4 w-4" />
                      <span>{selectedVacancy.contact_email}</span>
                    </div>
                    {selectedVacancy.contact_phone && (
                      <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        <Phone className="h-4 w-4" />
                        <span>{selectedVacancy.contact_phone}</span>
                      </div>
                    )}
                    {selectedVacancy.deadline && (
                      <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        <Clock className="h-4 w-4" />
                        <span>Deadline: {selectedVacancy.deadline}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]"
                  onClick={() => handleStartApplication(selectedVacancy)}
                >
                  Solliciteer Nu
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Form Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
          {selectedVacancy && (
            <>
              <DialogHeader>
                <DialogTitle className={`font-['Chivo'] text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Solliciteren: {selectedVacancy.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Naam *</Label>
                    <Input
                      value={applicationForm.name}
                      onChange={(e) => setApplicationForm({...applicationForm, name: e.target.value})}
                      placeholder="Je volledige naam"
                      className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email *</Label>
                    <Input
                      type="email"
                      value={applicationForm.email}
                      onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                      placeholder="je@email.com"
                      className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Telefoon</Label>
                  <Input
                    value={applicationForm.phone}
                    onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                    placeholder="+32 xxx xx xx xx"
                    className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Motivatie *</Label>
                  <Textarea
                    value={applicationForm.motivation}
                    onChange={(e) => setApplicationForm({...applicationForm, motivation: e.target.value})}
                    placeholder="Vertel ons waarom je geïnteresseerd bent in deze functie en wat je kunt bijdragen..."
                    rows={5}
                    className={isDark ? 'bg-slate-800 border-white/10' : 'border-slate-200'}
                    data-testid="application-motivation"
                  />
                </div>

                {/* CV Upload */}
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>CV Uploaden (PDF of Word)</Label>
                  
                  {applicationForm.cv_filename ? (
                    <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-800 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                      <div className="flex items-center gap-2">
                        <FileText className={`h-5 w-5 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                        <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{applicationForm.cv_filename}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveCV}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${isDark ? 'border-white/10 hover:border-white/20' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="cv-upload"
                        disabled={uploading}
                      />
                      <label htmlFor="cv-upload" className="cursor-pointer">
                        <Upload className={`h-10 w-10 mx-auto mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {uploading ? "Uploaden..." : "Klik om je CV te uploaden"}
                        </p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          PDF of Word, max 5MB
                        </p>
                      </label>
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setApplyDialogOpen(false)} disabled={submitting}>
                  Annuleren
                </Button>
                <Button 
                  onClick={handleSubmitApplication} 
                  disabled={submitting}
                  className="bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]"
                >
                  {submitting ? "Versturen..." : "Sollicitatie Versturen"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Vacatures() {
  return (
    <RecaptchaWrapper>
      <VacaturesPage />
    </RecaptchaWrapper>
  );
}
